import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function SplashIntro({
  bannerSrc = '/splash.png',
  bannerVideo = null, // optional video background
  onSubmitEmail,
  onClose,
  appReady = false,
}) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const headline = useMemo(
    () => 'Future AI Alliance – Quant Terminal',
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter an email address.');
      return;
    }
    try {
      setError(null);
      setSubmitting(true);
      await onSubmitEmail?.(email.trim());
      setSubmitted(true);
    } catch (err) {
      const message = err?.message || 'Unable to save your email. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleProceed = () => {
    if (!appReady || submitting) return;
    onClose?.();
  };

  const statusChip = (
    <Chip
      size="small"
      label={appReady ? 'Data ready' : 'Preparing data…'}
      color={appReady ? 'success' : 'default'}
      icon={
        !appReady ? <CircularProgress color="inherit" size={14} /> : undefined
      }
      sx={{ alignSelf: 'center' }}
    />
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal + 1,
        backgroundImage: bannerVideo
          ? 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.7) 100%)'
          : `linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.7) 100%), url(${bannerSrc})`,
        backgroundSize: bannerVideo ? 'cover' : '80% auto',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundColor: '#000',
        color: '#fff',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {bannerVideo && (
        <Box
          component="video"
          autoPlay
          muted
          loop
          playsInline
          src={bannerVideo}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.45,
          }}
        />
      )}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(1px)',
          px: 2,
          py: { xs: 2, sm: 3 },
        }}
      >
        <Paper
          elevation={10}
          sx={{
            width: '100%',
            maxWidth: 540,
            p: { xs: 3.5, sm: 4 },
            borderRadius: 3,
            backgroundColor: 'rgba(12,16,26,0.9)',
            color: '#e8ecf5',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(6px)',
            position: 'relative',
          }}
        >
          <IconButton
            aria-label="Close splash"
            onClick={handleProceed}
            size="small"
            disabled={!appReady}
            sx={{ position: 'absolute', top: 12, right: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Stack spacing={2}>
            {statusChip}

            <Stack spacing={1}>
              <Typography variant="h5" component="h1" color="inherit" align="center">
                {headline}
              </Typography>
              <Typography
                variant="body1"
                align="center"
                sx={{ color: 'rgba(232,236,245,0.78)' }}
              >
                {appReady
                  ? 'Your data is ready. Proceed to the app, or drop your email to stay notified about updates.'
                  : 'We are loading your trading data and models. Enter your email to get updates while you wait; you can proceed once data is ready.'}
              </Typography>
            </Stack>

            <form onSubmit={handleSubmit}>
              <Stack spacing={1.5}>
                <TextField
                  label="Work email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  disabled={submitting || submitted}
                  required
                  variant="filled"
                  color="primary"
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: 1.25,
                      color: '#fff',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                      },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(232,236,245,0.7)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#fff' },
                    '& .MuiInputBase-input': { color: '#fff' },
                  }}
                />

                {error && (
                  <Alert
                    severity="error"
                    variant="filled"
                    onClose={() => setError(null)}
                    sx={{ borderRadius: 1.5 }}
                  >
                    {error}
                  </Alert>
                )}

                {submitted && !error && (
                  <Alert severity="success" variant="filled" sx={{ borderRadius: 1.5 }}>
                    Thanks! We will keep you posted.
                  </Alert>
                )}

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={submitting || submitted}
                    sx={{ fontWeight: 600 }}
                  >
                    {submitting ? 'Submitting…' : submitted ? 'Submitted' : 'Submit email'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    fullWidth
                    onClick={handleProceed}
                    disabled={submitting || !appReady}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.5)',
                      color: '#fff',
                      '&:hover': {
                        borderColor: '#fff',
                        backgroundColor: 'rgba(255,255,255,0.08)',
                      },
                    }}
                  >
                    {appReady ? 'Proceed to app' : 'Preparing data…'}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}

export default SplashIntro;

